import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import { SwitchToggleV2 } from 'core-system/SwitchToggle'
import Radio from 'core-system/Radio'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { Copy } from 'core-system/Icons/Actions'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import pxToRem from 'core-system/utils/pxToRem'
import { AppState } from 'redux/config/store'
import RandomUtils from 'shared/RandomUtils'
import SyncSettingsCollapsible from 'features/Segments/syncSettings/SyncSettingsCollapsible'
import { apiRosterSyncActions } from './apiRosterSyncSlice'
import { RevocationCadence } from './apiRosterSyncTypes'
import {
  CADENCE_OPTIONS,
  formatKeyValidity,
  formatRevealValidity,
  getEmployerCsvUploadUrl,
  isActiveUnexpiredKey,
} from './apiRosterSyncUtils'

const SettingRow = styled(FlexContainer)`
  padding: 1.5rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey300};
  gap: 1.5rem;

  &:last-child {
    border-bottom: none;
  }
`

const SettingLabel = styled.div`
  flex: 1;
  min-width: 0;
`

const RadioOption = styled(FlexContainer)`
  cursor: pointer;
`

const RevealBanner = styled.div`
  width: 100%;
  margin-bottom: 1.25rem;
  padding: 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  background: ${(props) => props.theme.palette.grey.grey100};
`

const KeyChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin: 0.75rem 0 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.secondary.purple200};
`

const KeyValue = styled.code`
  flex: 1;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${(props) => props.theme.palette.text.primary};
  overflow-wrap: anywhere;
  user-select: all;
`

const CopyIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${(props) => props.theme.palette.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.palette.primary.pink800};
    background: ${(props) => props.theme.palette.primary.pink100};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.palette.secondary.purple400};
    outline-offset: 2px;
  }
`

const KeyRow = styled(FlexContainer)`
  padding: 0.85rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey200};

  &:last-child {
    border-bottom: none;
  }
`

const cadenceStatus = (enabled: boolean, cadence: RevocationCadence) => {
  if (!enabled) return 'Off'
  const option = CADENCE_OPTIONS.find((o) => o.value === cadence)
  return `On · ${option?.label ?? cadence}`
}

const ApiRosterSyncSection: React.FC = () => {
  const dispatch = useDispatch()
  const userGroups = useSelector((state: AppState) => state.user.profile.groups)
  const employer = useSelector((state: AppState) => state.employer.profile)
  const { settings, keys, rawKey, rawKeyExpiresAt, saving } = useSelector(
    (state: AppState) => state.apiRosterSync
  )
  const isHrAdmin = (userGroups || []).includes('HRadmins')
  const [revokeId, setRevokeId] = useState<string | null>(null)

  useEffect(() => {
    dispatch(apiRosterSyncActions.getRosterSync())
    return () => {
      dispatch(apiRosterSyncActions.dismissRawKey())
    }
  }, [dispatch])

  const activeKeys = keys.filter(isActiveUnexpiredKey)
  const canGenerate = isHrAdmin && settings.enabled && activeKeys.length < 2
  const finchConnected = Boolean(employer?.payrollProviderId)

  const handleToggle = () => {
    if (!isHrAdmin) return
    dispatch(
      apiRosterSyncActions.updateRosterSyncSettings({
        enabled: !settings.enabled,
      })
    )
  }

  const handleCadence = (cadence: RevocationCadence) => {
    if (!isHrAdmin) return
    dispatch(
      apiRosterSyncActions.updateRosterSyncSettings({
        revocationCadence: cadence,
      })
    )
  }

  return (
    <>
      <SyncSettingsCollapsible
        title='API roster sync'
        status={`${cadenceStatus(settings.enabled, settings.revocationCadence)}${
          settings.enabled ? ` · ${activeKeys.length} active key${activeKeys.length === 1 ? '' : 's'}` : ''
        }`}
      >
        <SettingRow alignItems='center' justifyContent='space-between'>
          <SettingLabel>
            <Text variant='action3' marginBottom='0.25rem'>
              Enable API roster sync
            </Text>
            <Text variant='body2' color='grey600'>
              Commuters are updated through the Fleet CSV ingest API. Add
              Commuters (CSV / HRIS) is hidden while this is on.
            </Text>
          </SettingLabel>
          <SwitchToggleV2
            active={settings.enabled}
            onClick={handleToggle}
            isDisabled={!isHrAdmin}
            size='large'
          />
        </SettingRow>

        {settings.enabled && (
          <>
            {finchConnected && (
              <SettingRow>
                <Text variant='body2' color='grey600'>
                  You are connected to a payroll provider. Enabling API roster
                  sync hides Add Commuters; it does not disconnect HRIS.
                </Text>
              </SettingRow>
            )}

            <SettingRow flexDirection='column'>
              <Text variant='action3' marginBottom='0.5rem'>
                Ingest endpoint
              </Text>
              <Text variant='body2' color='grey600'>
                POST your roster CSV to this URL. It is specific to this Fleet
                environment (production, staging, or local).
              </Text>
              <KeyChip data-testid='api-roster-ingest-url'>
                <KeyValue>{getEmployerCsvUploadUrl()}</KeyValue>
                <CopyIconButton
                  type='button'
                  aria-label='Copy ingest endpoint'
                  onClick={() =>
                    RandomUtils.copyToClipboard(getEmployerCsvUploadUrl())
                  }
                >
                  <Copy width={18} height={18} />
                </CopyIconButton>
              </KeyChip>
            </SettingRow>

            <SettingRow flexDirection='column'>
              <Text variant='action3' marginBottom='0.5rem'>
                Revocation cadence
              </Text>
              <Text variant='body2' color='grey600' marginBottom='1rem'>
                We email you 14 days before a key expires so you can generate an
                overlapping key. Cadence applies to newly generated keys only.
              </Text>
              {CADENCE_OPTIONS.map((option) => (
                <RadioOption
                  key={option.value}
                  alignItems='flex-start'
                  marginBottom='1rem'
                  onClick={() => handleCadence(option.value)}
                >
                  <Radio
                    active={settings.revocationCadence === option.value}
                    disabled={!isHrAdmin}
                    label={
                      <FlexContainer flexDirection='column'>
                        <Text variant='action4'>{option.label}</Text>
                        <Text variant='body2' color='grey600'>
                          {option.description}
                        </Text>
                      </FlexContainer>
                    }
                  />
                </RadioOption>
              ))}
            </SettingRow>

            <SettingRow flexDirection='column'>
              <Text variant='action3' marginBottom='0.75rem'>
                API keys
              </Text>
              {rawKey && (
                <RevealBanner data-testid='api-key-reveal'>
                  <Text variant='action4'>
                    Copy this key now — it will not be shown again
                  </Text>
                  <KeyChip>
                    <KeyValue>{rawKey}</KeyValue>
                    <CopyIconButton
                      type='button'
                      aria-label='Copy API key'
                      onClick={() => RandomUtils.copyToClipboard(rawKey)}
                    >
                      <Copy width={18} height={18} />
                    </CopyIconButton>
                  </KeyChip>
                  <Text variant='body2' color='grey600' marginBottom='1rem'>
                    {formatRevealValidity(rawKeyExpiresAt)}
                  </Text>
                  <PrimaryButton
                    onClick={() =>
                      dispatch(apiRosterSyncActions.dismissRawKey())
                    }
                  >
                    I have copied this key
                  </PrimaryButton>
                </RevealBanner>
              )}
              {keys.map((key) => (
                <KeyRow
                  key={key.id}
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <div>
                    <Text variant='action4'>
                      {key.keyPrefix}…
                    </Text>
                    <Text variant='body2' color='grey600'>
                      {key.revokedAt
                        ? 'Revoked'
                        : formatKeyValidity(key.expiresAt)}
                      {key.lastUsedAt
                        ? ` · Last used ${new Date(
                            key.lastUsedAt
                          ).toLocaleDateString()}`
                        : ''}
                    </Text>
                  </div>
                  {isHrAdmin && !key.revokedAt && (
                    <SecondaryButton onClick={() => setRevokeId(key.id)}>
                      Revoke
                    </SecondaryButton>
                  )}
                </KeyRow>
              ))}
              {isHrAdmin && (
                <FlexContainer
                  marginTop='1rem'
                  flexDirection='column'
                  alignItems='flex-start'
                >
                  <PrimaryButton
                    onClick={() =>
                      dispatch(apiRosterSyncActions.generateApiKey())
                    }
                    disabled={!canGenerate}
                    isLoading={saving}
                  >
                    {keys.some(isActiveUnexpiredKey)
                      ? 'Generate overlapping key'
                      : 'Generate API key'}
                  </PrimaryButton>
                  {activeKeys.length >= 2 && (
                    <Text variant='body2' color='grey600' marginTop='0.5rem'>
                      At most 2 active keys. Revoke one to generate another.
                    </Text>
                  )}
                </FlexContainer>
              )}
            </SettingRow>
          </>
        )}
      </SyncSettingsCollapsible>

      <Modal
        open={Boolean(revokeId)}
        onClose={() => setRevokeId(null)}
        width={pxToRem(480)}
      >
        <ModalHeader title='Revoke API key' />
        <ModalBody>
          <Text variant='body1'>
            The old key will stop working immediately. Generate an overlapping
            key first if you still need ingest access.
          </Text>
        </ModalBody>
        <ModalFooter>
          <SecondaryButton
            style={{ marginRight: '1rem' }}
            onClick={() => setRevokeId(null)}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              if (revokeId) {
                dispatch(apiRosterSyncActions.revokeApiKey(revokeId))
              }
              setRevokeId(null)
            }}
          >
            Revoke key
          </PrimaryButton>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default React.memo(ApiRosterSyncSection)
